import { Dialog as SheetPrimitive } from 'radix-ui'
import { XIcon } from 'lucide-react'
import type { ComponentProps } from 'react'

import { cn } from '@/lib/utils'

/**
 * The default surface for record detail. A sheet keeps the list visible behind it,
 * so an operator never loses their place in a filtered view to inspect one row.
 * Modals are reserved for genuine interruptions.
 */
const Sheet = SheetPrimitive.Root
const SheetTrigger = SheetPrimitive.Trigger
const SheetClose = SheetPrimitive.Close

function SheetContent({
    className,
    children,
    side = 'right',
    showCloseButton = true,
    onOpenAutoFocus,
    ...props
}: ComponentProps<typeof SheetPrimitive.Content> & {
    side?: 'top' | 'right' | 'bottom' | 'left'
    showCloseButton?: boolean
}) {
    return (
        <SheetPrimitive.Portal>
            <SheetPrimitive.Overlay
                data-slot="sheet-overlay"
                className={cn(
                    'fixed inset-0 z-50 bg-black/40 dark:bg-black/60',
                    'data-[state=open]:animate-in data-[state=open]:fade-in-0',
                    'data-[state=closed]:animate-out data-[state=closed]:fade-out-0'
                )}
            />
            <SheetPrimitive.Content
                data-slot="sheet-content"
                // Focusable so the panel itself can take focus on open, below.
                tabIndex={-1}
                onOpenAutoFocus={event => {
                    onOpenAutoFocus?.(event)
                    if (event.defaultPrevented) return

                    /*
                     * Radix otherwise focuses the first tabbable descendant, which in a
                     * record sheet is whatever happens to come first -- usually a copy
                     * button or a status badge, halfway down the header. That reads as
                     * arbitrary, tabbing then continues from the middle of the panel,
                     * and a screen reader is told about that one control rather than
                     * about the sheet it just opened.
                     *
                     * Focusing the panel announces its title and starts the tab order
                     * at the top. Forms inside a sheet cost one Tab to reach.
                     */
                    event.preventDefault()
                    ;(event.currentTarget as HTMLElement | null)?.focus({ preventScroll: true })
                }}
                className={cn(
                    'fixed z-50 flex flex-col gap-0 bg-card shadow-xl',
                    'transition ease-(--ease-out-quart) data-[state=closed]:duration-(--animate-duration) data-[state=open]:duration-200',
                    // `h-dvh` rather than `h-full`: mobile browsers grow and shrink the visual
                    // viewport as their chrome hides, and `100%` of a fixed box lags behind it.
                    // Identical to `h-full` on desktop, where the two never differ.
                    //
                    // Below `sm` the right sheet leaves a 2.5rem strip of overlay: at full width
                    // there is nothing left to tap to dismiss, and a phone has no Escape key.
                    side === 'right' &&
                        'inset-y-0 right-0 h-dvh w-full max-sm:w-[calc(100%-2.5rem)] border-l border-border sm:max-w-xl data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right',
                    side === 'left' &&
                        'inset-y-0 left-0 h-dvh w-full border-r border-border sm:max-w-sm data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left',
                    side === 'top' &&
                        'inset-x-0 top-0 h-auto border-b border-border data-[state=closed]:slide-out-to-top data-[state=open]:slide-in-from-top',
                    side === 'bottom' &&
                        'inset-x-0 bottom-0 h-auto border-t border-border data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom',
                    'data-[state=open]:animate-in data-[state=closed]:animate-out',
                    'outline-none',
                    className
                )}
                {...props}>
                {children}
                {showCloseButton && (
                    <SheetPrimitive.Close
                        className={cn(
                            // On a phone this is the primary way out, so it grows for real under a
                            // coarse pointer. The header already reserves `pr-12` for it.
                            'absolute right-3 top-3 inline-flex size-7 items-center justify-center rounded-md text-muted-foreground',
                            'pointer-coarse:size-11 pointer-coarse:right-1.5 pointer-coarse:top-1.5',
                            'transition-colors hover:bg-surface-hover hover:text-foreground',
                            'outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring',
                            'disabled:pointer-events-none'
                        )}>
                        <XIcon className="size-4" />
                        <span className="sr-only">Close</span>
                    </SheetPrimitive.Close>
                )}
            </SheetPrimitive.Content>
        </SheetPrimitive.Portal>
    )
}

function SheetHeader({ className, ...props }: ComponentProps<'div'>) {
    return (
        <div
            data-slot="sheet-header"
            className={cn('flex shrink-0 flex-col gap-1 border-b border-border px-5 py-4 pr-12', className)}
            {...props}
        />
    )
}

function SheetBody({ className, ...props }: ComponentProps<'div'>) {
    return (
        <div
            data-slot="sheet-body"
            className={cn('scrollbar-thin min-h-0 flex-1 overflow-y-auto px-5 py-5', className)}
            {...props}
        />
    )
}

function SheetFooter({ className, ...props }: ComponentProps<'div'>) {
    return (
        <div
            data-slot="sheet-footer"
            className={cn(
                // The footer sits on the bottom edge, where a phone's home indicator overlaps it.
                // `max()` resolves to today's 0.75rem wherever there is no inset, desktop included.
                'flex shrink-0 items-center justify-end gap-2 border-t border-border px-5 py-3',
                'pb-[max(0.75rem,env(safe-area-inset-bottom))]',
                className
            )}
            {...props}
        />
    )
}

function SheetTitle({ className, ...props }: ComponentProps<typeof SheetPrimitive.Title>) {
    return (
        <SheetPrimitive.Title
            data-slot="sheet-title"
            className={cn('text-base font-semibold leading-tight tracking-tight', className)}
            {...props}
        />
    )
}

function SheetDescription({ className, ...props }: ComponentProps<typeof SheetPrimitive.Description>) {
    return (
        <SheetPrimitive.Description
            data-slot="sheet-description"
            className={cn('text-sm text-muted-foreground', className)}
            {...props}
        />
    )
}

export {
    Sheet,
    SheetBody,
    SheetClose,
    SheetContent,
    SheetDescription,
    SheetFooter,
    SheetHeader,
    SheetTitle,
    SheetTrigger
}
